import { useCallback, useEffect, useState } from 'react';

/**
 * Le brouillon, conservé d'une session à l'autre — #24, #86.
 *
 * ## Ce que ça remplace, et ce que ça n'est pas
 *
 * Le brouillon était un `useState` : perdu à chaque rechargement, **même en
 * ligne**. Sur un réseau d'hôtel, il l'était donc exactement quand il coûtait le
 * plus cher — après un envoi qui échoue.
 *
 * ⚠️ **Ce n'est PAS une file d'envoi**, et c'est une décision de produit, pas une
 * limite technique. Un envoi différé automatique est un message que l'utilisateur
 * n'a pas choisi d'envoyer à ce moment-là, et **en hôtellerie l'heure fait le
 * sens** : « j'arrive dans 5 minutes » délivré quatre heures plus tard est devenu
 * faux.
 *
 * Le texte est donc gardé, l'envoi reste **à un geste**, et rien ne part sans que
 * quelqu'un l'ait décidé. Corollaire utile : pas de réessai automatique, donc pas
 * de double envoi de ce côté.
 *
 * Le « jamais de cul-de-sac » de #19 est tenu — rien n'est perdu, et le chemin
 * qui aboutit est à un tap.
 *
 * ## La clé
 *
 * Par utilisateur ET par client : `mobile_brouillon_{userId}_{customerId}`. Une
 * session EXPIRÉE garde les brouillons (#18) ; un collègue qui se connecte ensuite
 * sur le même téléphone ne doit ni voir ni envoyer le texte de l'autre. Tant que
 * l'utilisateur n'est pas connu (id 0 du contexte par défaut), rien n'est écrit :
 * le brouillon reste volatil.
 */

const prefixe = 'mobile_brouillon_';

const cleDe = (userId: number | undefined, customerId: number | string | undefined) =>
    userId && customerId ? `${prefixe}${userId}_${customerId}` : null;

/**
 * Lit le brouillon de `cle`. Un brouillon écrit avant la clé par utilisateur
 * (`mobile_brouillon_{customerId}`) est repris une fois sous la nouvelle clé : il
 * ne doit pas disparaître au déploiement. Idempotent (StrictMode rejoue l'appel).
 */
function lireBrouillon(cle: string, customerId: number | string): string {
    const actuel = localStorage.getItem(cle);
    if (actuel !== null) return actuel;

    const cleAncienne = `${prefixe}${customerId}`;
    const ancien = localStorage.getItem(cleAncienne);
    if (ancien === null) return '';
    localStorage.setItem(cle, ancien);
    localStorage.removeItem(cleAncienne);
    return ancien;
}

export function useBrouillonPersistant(
    userId: number | undefined,
    customerId: number | string | undefined,
): [string, (valeur: string) => void, () => void] {
    const cle = cleDe(userId, customerId);

    const [valeur, setValeur] = useState(() => {
        if (!cle || customerId === undefined) return '';
        try {
            return lireBrouillon(cle, customerId);
        } catch {
            // Stockage refusé : on retombe sur un brouillon volatil, ce qui est
            // le comportement d'avant. Jamais une erreur à l'écran pour ça.
            return '';
        }
    });

    // Le client (ou l'utilisateur) peut changer sous le composant : sans ça, le
    // brouillon du précédent s'afficherait sous le nom du suivant.
    useEffect(() => {
        if (!cle || customerId === undefined) {
            setValeur('');
            return;
        }
        try {
            setValeur(lireBrouillon(cle, customerId));
        } catch {
            setValeur('');
        }
    }, [cle, customerId]);

    const ecrire = useCallback(
        (nouvelle: string) => {
            setValeur(nouvelle);
            if (!cle) return;
            try {
                // Un brouillon vide n'est pas un brouillon : on retire la clé
                // plutôt que d'accumuler des chaînes vides par conversation.
                if (nouvelle === '') localStorage.removeItem(cle);
                else localStorage.setItem(cle, nouvelle);
            } catch {
                /* Stockage plein ou refusé : le texte reste à l'écran. */
            }
        },
        [cle],
    );

    /** Après un envoi réussi : le brouillon n'a plus de raison d'exister. */
    const vider = useCallback(() => ecrire(''), [ecrire]);

    return [valeur, ecrire, vider];
}

/**
 * Efface tous les brouillons de l'appareil, à la déconnexion (audit 2026-09-23). Un
 * brouillon est du contenu client : il ne survit pas au départ de celui qui l'a
 * écrit. Une session EXPIRÉE, elle, les garde (#18 : l'expiration n'est pas une
 * intention).
 */
export function viderTousLesBrouillons(): void {
    try {
        Object.keys(localStorage)
            .filter((k) => k.startsWith(prefixe))
            .forEach((k) => localStorage.removeItem(k));
    } catch {
        /* Stockage refusé : il n'y a rien à effacer. */
    }
}
